// VerificationControls.jsx
//
// UI convenience for the governance transitions. The server-side
// guard is authoritative; hiding a button is only cosmetic. Any actor
// without `can_verify_choral_repertoire` still fails at the API.
import React, { useState } from "react";

const NEXT_STATES = {
  draft:           [{ id: "ai_reviewed",     label: "Move to AI Reviewed" }],
  ai_reviewed:     [{ id: "editor_reviewed", label: "Move to Editor Reviewed" },
                    { id: "draft",           label: "Send back to Draft" }],
  editor_reviewed: [{ id: "verified",        label: "Verify (Owner Only)", ownerOnly: true },
                    { id: "ai_reviewed",     label: "Send back to AI Reviewed" }],
  verified:        [],  // Verified stays verified. Archive is separate.
  archived:        [],
};

export default function VerificationControls({
  work,
  canVerify,
  onChanged,
  apiBase = "/api/admin/choral",
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const status =
    typeof work.verification_status === "string"
      ? work.verification_status
      : work.verification_status?.value;
  const options = NEXT_STATES[status] || [];

  async function transition(newStatus) {
    const reason = window.prompt(`Reason for moving to "${newStatus}":`);
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/${work.id}/transition`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_status: newStatus, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      onChanged?.(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (status === "archived") return;
    const reason = window.prompt("Reason for archiving:");
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/${work.id}/transition`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_status: "archived", reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      onChanged?.(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="ce-governance-controls">
      <p style={{ margin: 0, fontFamily: "Inter, sans-serif", fontSize: "0.92rem" }}>
        Current state:&nbsp;
        <strong data-testid="ce-current-status">{status}</strong>
      </p>
      <div className="ce-governance__actions">
        {options.map((opt) => {
          if (opt.ownerOnly && !canVerify) return null;
          return (
            <button
              key={opt.id}
              className={opt.id === "verified" ? "ce-primary" : "ce-secondary"}
              disabled={busy}
              onClick={() => transition(opt.id)}
              data-testid={`ce-transition-${opt.id}`}
            >
              {opt.label}
            </button>
          );
        })}
        {status !== "archived" && (
          <button
            className="ce-secondary"
            disabled={busy}
            onClick={archive}
            data-testid="ce-transition-archived"
          >
            Archive
          </button>
        )}
      </div>
      {error && (
        <p style={{ marginTop: 10, color: "#8A5A32", fontSize: "0.85rem" }}>
          {error}
        </p>
      )}
    </div>
  );
}
